import { LayoutGroup } from 'motion/react';
import { twMerge } from 'tailwind-merge';

import StepIndicator from './StepIndicator';
import ConditionalRenderer from '../ConditionalRenderer';
import { StepClassnames } from '@/utils/ClassNameComponent';

type StepsProps = {
  activeStep?: number;
  steps: Step[];
  concludedOnce?: boolean;
  disabled?: boolean;
  hideIndicator?: boolean;
  loading?: boolean;
  className?: StepClassnames;
};

export type StepStatus =
  | 'done'
  | 'todo'
  | 'active'
  | 'skipped'
  | 'error'
  | 'success'
  | 'info'
  | 'loading';

export type Step = {
  id: number;
  status: StepStatus;
  content?: string | JSX.Element;
  caption?: string;
  onClick?(index: number): void | Promise<void>;
};

export default function Steps({
  activeStep = 0,
  steps,
  disabled,
  hideIndicator,
  loading,
  className,
}: StepsProps) {
  return (
    <div
      data-testid="steps"
      className={twMerge(
        'relative scrollbar-thin scrollbar-h-0.5 scrollbar-thumb-primary/40 scrollbar-track-primary-content scrollbar-thumb-rounded-full scrollbar-track-rounded-full font-rubik font-500 text-18 flex w-full py-2 transition ease-in-out duration-150 overflow-y-hidden',
        disabled ? 'disabled' : '',
        className?.base,
      )}
    >
      <LayoutGroup>
        {steps.map((step, index, array) => {
          const { stepClassName, content } = stepStatus({
            step: { ...step, status: loading ? 'loading' : step.status },
            className,
          });
          const onClick = () => step.onClick?.(index);
          const baseClassName = twMerge(
            step.onClick ? 'cursor-pointer clickable' : 'pointer-events-none',
            'rounded-full w-8 h-8 flex items-center justify-center z-10',
            'transform hover:scale-110',
            step.caption ? 'text-12' : 'text-20',
            'transition ease-in-out duration-150',
            className?.step,
            stepClassName,
          );

          const pathColorStatus: StepStatus[] = [
            'success',
            'error',
            'skipped',
            'info',
          ];

          const lastIndexDone = steps
            .map((step, index) => ({ index, status: step.status }))
            .reduce(
              (lastIndex, step) =>
                step.status === 'done' ? step.index : lastIndex,
              -1,
            );

          const lineLeftColorCondition =
            index <= activeStep ||
            pathColorStatus.includes(steps[index + 1]?.status) ||
            pathColorStatus.includes(step.status) ||
            lastIndexDone >= index;

          const lineRightColorCondition =
            index < activeStep ||
            pathColorStatus.includes(step.status) ||
            lastIndexDone > index;

          return (
            <div
              key={step.id}
              className="flex flex-col items-center gap-1 min-w-[60px] w-full relative"
            >
              <span
                data-testid={`step${step.id}`}
                onClick={onClick}
                className={baseClassName}
                data-content={content}
              >
                <ConditionalRenderer
                  condition={step.status !== 'loading' && !loading}
                >
                  {step.caption ?? content ?? index + 1}
                </ConditionalRenderer>
              </span>
              <div className="absolute grid grid-cols-2 w-full h-[32px] items-center">
                <hr
                  data-testid="left"
                  className={twMerge(
                    'h-2',
                    index > 0 ? '' : 'invisible',
                    lineLeftColorCondition && !loading
                      ? 'bg-accent border-accent'
                      : 'bg-accent-content border-accent-content',
                    lineLeftColorCondition && !loading
                      ? className?.doneLine
                      : className?.todoLine,
                    loading ? 'animate-pulse' : '',
                  )}
                />
                <hr
                  data-testid="right"
                  className={twMerge(
                    'h-2',
                    index < array.length - 1 ? '' : 'invisible',
                    lineRightColorCondition && !loading
                      ? 'bg-accent border-accent'
                      : 'bg-accent-content border-accent-content',
                    lineRightColorCondition && !loading
                      ? className?.doneLine
                      : className?.todoLine,
                    loading ? 'animate-pulse' : '',
                  )}
                />
              </div>
              <ConditionalRenderer
                condition={activeStep === index && !hideIndicator}
              >
                <StepIndicator
                  layoutId="stepIndicator"
                  className={twMerge(
                    loading ? 'invisible' : '',
                    className?.indicator,
                  )}
                />
              </ConditionalRenderer>
            </div>
          );
        })}
      </LayoutGroup>
    </div>
  );
}

type StepStatusProps = {
  step: Step;
  loadingActivityProgress?: boolean;
  className?: StepClassnames;
};

type StepFormat = {
  content?: string | JSX.Element;
  stepClassName?: string;
};

function stepStatus({ step, className }: StepStatusProps): Step & StepFormat {
  const { status } = step;

  const active = twMerge(
    'bg-accent-content text-accent border-2 border-accent',
    className?.active,
  );
  const done = twMerge(
    'bg-accent text-accent-content border-2 border-accent',
    className?.done,
  );
  const info = twMerge('text-base-100 bg-primary', className?.info);
  const error = twMerge('text-base-100 bg-error', className?.error);
  const loading = twMerge(
    'bg-accent-content animate-pulse border-accent-content',
    className?.loading,
  );
  const neutral = twMerge('bg-accent-content text-accent', className?.todo);
  const success = 'text-base-100 bg-success';

  const stepFormats: {
    [key in StepStatus]: StepFormat;
  } = {
    skipped: {
      stepClassName: active,
    },
    success: { stepClassName: success, content: '✓' },
    error: { stepClassName: error, content: '✕' },
    info: { stepClassName: info },
    done: { stepClassName: done },
    loading: { stepClassName: loading },
    active: { stepClassName: active },
    todo: { stepClassName: neutral },
  };

  return {
    ...step,
    content: stepFormats[status].content ?? step.content,
    stepClassName: stepFormats[status].stepClassName,
  };
}
